/* ──────────────────────────────────────────────
 *  Booking Tunnel V2 — Types & Interfaces
 *
 *  Data model types are re-exported from @cfixe-monorepo/shared
 *  to avoid duplicating API schemas. Only tunnel-specific types
 *  (cart state, UI state, callbacks) are defined here.
 * ────────────────────────────────────────────── */

import type { ReactNode } from 'react';
import type {
  SiteEventParams,
  AddressObject,
  BookingBookabilityResponseObject,
  BookingPrestationItemObject,
  BookingPriceOptionObject,
  BookingSelectableResourceObject,
  BookingTeamMemberObject,
  PublicCategoryObject,
  PublicPlaceObject,
  PublicPrestationSupplementObject,
  BookableSlotObject,
  BookableSlotItemObject,
  BookableNextSlotResponseObject,
  BookingAmountsObject,
  CartSummaryResponseObject,
  CartSummaryItemObject,
  BookingPromoIneligibilityReason,
  SupplementQuantity,
} from '@cfixe-monorepo/shared';
import { MAPPED_TRANSLATION, public_env } from '@cfixe-monorepo/shared';
import { paymentTimelineLabelsFor, type PaymentTimelineLabels } from './components/shared/payment-timeline-labels';
import { lowercaseKeys, tunnelTranslation, type LowercasedLabels } from './utils/tunnel-translation';

export type { AddressObject };
export type { PaymentTimelineLabels };

export interface AddressAutocompletePrediction {
  place_id: string;
  description: string;
}

// ── Step identifiers ────────────────────────────

/*
 * Membre et ressource n'ont plus d'écran (spec Parcours P3) : ils se choisissent au-dessus du calendrier
 * de l'étape `slot`. `quote` : formulaire de demande de devis d'une prestation sur devis, hors parcours.
 */
export type BookingStep = 'services' | 'place' | 'zone_address' | 'slot' | 'checkout' | 'quote' | 'success';

/**
 * Mode du parcours (spec Parcours § 5) : `express`, une prestation connue et complète (créneau puis
 * finalisation, lieu au-dessus du calendrier) ; `standard`, le catalogue d'abord ; `multi`, deux prestations
 * ou plus. Express et standard deviennent multi au deuxième ajout, jamais l'inverse sans action du client.
 */
export type BookingEntryMode = 'express' | 'standard' | 'multi';

/** Bandeau de l'étape créneau après un retour forcé : créneau pris pendant la saisie, ou pré-réservation expirée. */
export type BookingSlotNotice = 'taken' | 'expired';

// ── Re-export API types as tunnel aliases ───────

/** Booking prestation — API type with `prices` guaranteed non-optional for the tunnel context */
export type BookingPrestation = BookingPrestationItemObject & { prices: BookingPriceOptionObject[] };
export type Category = PublicCategoryObject;
export type PublicPlace = PublicPlaceObject;
export type TeamMember = BookingTeamMemberObject;
/* Option de prix du TUNNEL : porte en plus `is_bookable` / `first_bookable_date` par durée. */
export type PriceOption = BookingPriceOptionObject;
/* Verdict de réservabilité renvoyé par le recalcul live (prestation + durées sondées). */
export type PrestationBookability = BookingBookabilityResponseObject['prestations'][number];
export type Supplement = PublicPrestationSupplementObject;
export type BookableSlot = BookableSlotObject;
export type BookableSlotItem = BookableSlotItemObject;
/** Prochain créneau d'une semaine vide (P3), dates formatées serveur. */
export type BookableNextSlot = BookableNextSlotResponseObject;
export type CartSummary = CartSummaryResponseObject;
/** Récap du tunnel (remises, bons, à payer en ligne, reste sur place), formaté serveur. */
export type BookingAmounts = BookingAmountsObject;
/**
 * Contexte optionnel du récapitulatif : début ISO du (premier) créneau choisi, email et téléphone (E.164)
 * saisis, et le code tapé dans le champ unique (résolu par le serveur en code promo ou en carte cadeau).
 */
export interface CartSummaryContext {
  slot_start?: string;
  email?: string;
  phone?: string;
  code?: string;
  /** Lieu choisi : une promotion peut être limitée à certains lieux. */
  pro_place_id?: number;
}

/** Codes posés sur la réservation : un code promo et une carte cadeau (ou un bon) au plus. */
export interface AppliedCodes {
  promo?: string;
  voucher?: string;
}

/**
 * Origine de la remise de `payment_infos.discount_amount` : la carte cadeau quand il y en a une (seule
 * elle est un coupon, restitué ou perdu à l'annulation selon la politique du pro), sinon le code promo,
 * qui n'est qu'une remise.
 */
export type BookingDiscountSource = 'voucher' | 'promo';

export type CartSummaryItem = CartSummaryItemObject;
export type SelectableResource = BookingSelectableResourceObject;

// ── Selected service (cart item) ────────────────

export interface SelectedSupplement extends PublicPrestationSupplementObject {
  selected: boolean;
  /**
   * Quantité choisie au stepper (supplément `quantity_selectable` retenu). Absente tant que le
   * client n'y a pas touché : le supplément suit alors les unités facturées de la ligne.
   */
  quantity?: number;
}

export interface CartItem {
  prestation: BookingPrestation;
  selectedPrice: PriceOption;
  selectedPriceIndex: number;
  supplements: SelectedSupplement[];
  quantity: number;
  party_size?: number;
  selected_resource_id?: number;
}

// ── Member assignment ───────────────────────────

export interface MemberAssignment {
  prestationId: number;
  cartIndex: number;
  memberId: number | undefined; // undefined = "sans préférence"
  /** Explicit picks for prestations requiring N>=2 members. When set, takes precedence over memberId. */
  memberIds?: number[];
}

// ── Resource assignment ─────────────────────────

export interface ResourceAssignment {
  prestationId: number;
  cartIndex: number;
  pro_resource_id: number | undefined; // undefined = pas encore choisi
}

// ── Pre-reservation ─────────────────────────────

export interface PreReservationResponse {
  reservation_uuid: string;
  payment_type?: string;
  expires_at?: string;
  payment_infos?: {
    total_amount: string;
    total_amount_raw?: number;
    amount_to_pay_today: string;
    amount_to_pay_today_raw?: number;
    remaining_amount_on_site: string;
    deposit_amount?: string;
    /** Remise appliquée (carte cadeau OU code promo, cf. `discount_source`), formatée serveur. */
    discount_amount?: string;
    /** Total de la réservation après remise (payé aujourd'hui + reste sur place), formaté serveur. */
    total_after_discount?: string;
    possible_refund_in_time?: string;
    possible_refund_in_time_raw?: number;
    possible_refund_out_time?: string;
    possible_refund_out_time_raw?: number;
    cancellation_fee_in_time?: string;
    cancellation_fee_in_time_raw?: number;
    cancellation_fee_out_time?: string;
    cancellation_fee_out_time_raw?: number;
  };
  cancellation_deadline?: string;
  cancellation_deadline_formatted?: string;
  /** Politique pro snapshotée : le coupon est-il restitué si annulation avant deadline ? (undefined = legacy = oui) */
  voucher_refundable_on_cancel?: boolean;
  /** Origine de `payment_infos.discount_amount` ; absent = aucune remise. */
  discount_source?: BookingDiscountSource;
  /** Codes retenus par la pré-réservation (ils font foi pour les pastilles du champ code). */
  promo_code?: string;
  promo_code_label?: string;
  voucher_code?: string;
  /** Promotion automatique annoncée puis retirée à la validation du formulaire (condition client tombée). */
  promotion_rejected_reason?: BookingPromoIneligibilityReason;
  promotion_rejected_label?: string;
  /** Récap du tunnel ; absent d'un backend antérieur. */
  amounts?: BookingAmounts;
}

// ── Custom fields (pro-configured form) ─────────

export type CustomFieldType = 'text' | 'textarea' | 'select' | 'number' | 'checkbox' | 'date';

export interface BookingCustomField {
  custom_field_id: number;
  label: string;
  type: CustomFieldType;
  required: boolean;
  placeholder?: string;
  options?: string[];
  min?: number;
  max?: number;
  /** Vide / absent = aucune contrainte de prestation (champ global sur cette dimension). */
  prestation_ids?: number[];
  /**
   * Vide / absent = aucune contrainte de lieu (champ émis par un backend antérieur à la portée par
   * lieu compris). Non vide : le champ ne s'applique que si un lieu réservé figure dans la liste.
   */
  pro_place_ids?: number[];
  sort_order: number;
}

export interface CustomFieldAnswer {
  custom_field_id: number;
  value: string | number | boolean;
}

// ── Voucher response ────────────────────────────

export interface VoucherResponse {
  valid: boolean;
  message?: string;
  error_key?: string;
  /**
   * Statut HTTP du refus (`PublicApiError.status`). Seul un 400 sans `error_key` (validation du
   * corps) dit que le code n'a pas été examiné comme carte cadeau ; un 5xx ou un 429 ne dit rien du code.
   */
  status?: number;
  discount_amount?: string;
  new_total?: string;
  new_amount_to_pay?: string;
  updated_pre_reservation?: PreReservationResponse;
}

/** Pose d'un code de nature inconnue (champ unique) sur une pré-réservation (`PATCH :uuid/code`). */
export interface CodeResponse {
  valid: boolean;
  message?: string;
  error_key?: string;
  updated_pre_reservation?: PreReservationResponse;
}

// Miroir de VoucherResponse pour les codes promo (B2C).
export interface PromoResponse {
  valid: boolean;
  message?: string;
  error_key?: string;
  discount_amount?: string;
  new_total?: string;
  new_amount_to_pay?: string;
  updated_pre_reservation?: PreReservationResponse;
}

// ── Callbacks (provided by consumer app) ────────

export interface BookingCallbacks {
  /** Load prestations, categories, places, and team members in one call */
  onLoadPrestations: (params?: {
    prestation_ids?: number[];
    category_ids?: number[];
    place_ids?: number[];
    member_ids?: number[];
  }) => Promise<{
    categories: Category[];
    prestations: BookingPrestation[];
    team_members: TeamMember[];
    pro_places: PublicPlace[];
    custom_fields?: BookingCustomField[];
    booking_params?: BookingParams;
  }>;

  /** Compute cart summary (total + duration, formatted by backend) */
  onComputeCart: (
    items: {
      prestation_id: number;
      quantity: number;
      selected_price_index: number;
      /** Formule stable ; prime sur le rang côté serveur. */
      price_id?: number;
      selected_supplement_ids: number[];
      /** Quantités choisies des suppléments retenus (absent : une par unité facturée). */
      supplement_quantities?: SupplementQuantity[];
      party_size?: number;
      /** Début et membres de l'article une fois choisis : une promotion juge chaque ligne sur les siens. */
      slot_start?: string;
      team_member_ids?: number[];
    }[],
    voucherCode?: string,
    promoCode?: string,
    /** Créneau choisi, email et téléphone saisis : le serveur tranche le code promo tout de suite (cf. CartSummaryRequestSchema). */
    context?: CartSummaryContext,
  ) => Promise<CartSummary>;

  /**
   * Recalcul léger de la réservabilité des prestations candidates compte tenu du panier courant
   * (détection du conflit temporel multi-prestation). Gaté côté appelant : invoqué uniquement quand
   * un lieu effectif est connu ET que le panier multi-prestation est non vide. Renvoie le verdict
   * prestation ET, quand le backend a pu les sonder, le verdict par durée, ainsi que la 1re date
   * réservable (`first_bookable_date`, jour mural `YYYY-MM-DD`) re-sondée au lieu choisi — elle
   * sert à ouvrir la grille sur la bonne semaine.
   * Optionnel : si absent, le tunnel garde les drapeaux du chargement.
   */
  onCheckBookability?: (params: {
    slots: {
      prestation_id: number;
      duration?: number;
      team_member_id?: number;
      quantity?: number;
      selected_supplement_ids?: number[];
    }[];
    candidate_prestation_ids?: number[];
    pro_place_id?: number;
    /** Permet au tunnel d'annuler un appel live précédent (debounce). */
    signal?: AbortSignal;
  }) => Promise<PrestationBookability[]>;

  /**
   * Éligibilité membre par position (étape « choisir par membre », multi-prestation). Pour chaque
   * position « sans préférence » (team_member_id omis), renvoie les membres pouvant la servir dans une
   * chaîne réservable, conditionné par les membres déjà figés. Le tunnel grise les membres absents.
   * Optionnel : si absent, pas de grisé « combinaison sans créneau ».
   */
  onCheckEligibleMembers?: (params: {
    slots: {
      prestation_id: number;
      pro_place_id: number;
      duration: number;
      quantity?: number;
      team_member_id?: number;
    }[];
    signal?: AbortSignal;
  }) => Promise<{ index: number; eligible_member_ids: number[]; unconstrained: boolean }[]>;

  /** Load available time slots */
  onLoadSlots: (params: {
    start_date?: string;
    slots: {
      prestation_id: number;
      duration: number;
      team_member_id?: number;
      pro_place_id: number;
      quantity: number;
      selected_price_index: number;
      selected_supplement_ids: number[];
      pro_resource_ids?: number[];
      party_size?: number;
    }[];
    /* Séances de groupe : inclure aussi les créneaux sous-capacité (grisés côté UI). */
    include_session_slots_below_party_size?: boolean;
  }) => Promise<BookableSlot[]>;

  /**
   * Prochain créneau quand la semaine affichée est vide (P3) : une seule recherche, bornée à l'horizon du
   * pro, avec la même sélection que `onLoadSlots`. Optionnel : absent, la semaine vide n'offre que ses sorties.
   */
  onFindNextSlot?: (params: Parameters<BookingCallbacks['onLoadSlots']>[0]) => Promise<BookableNextSlot>;

  /** Create a pre-reservation */
  onPreReserve?: (params: {
    slots: {
      slot_start: string;
      slot_end: string;
      team_member_id?: number;
      team_member_ids?: number[];
      pro_place_id: number;
      prestation_id: number;
      quantity: number;
      selected_price_index?: number;
      selected_supplement_ids?: number[];
      /** Quantités choisies des suppléments retenus (absent : une par unité facturée). */
      supplement_quantities?: SupplementQuantity[];
      selected_resource_id?: number;
      party_size?: number;
    }[];
    customer: {
      firstname: string;
      lastname: string;
      email: string;
      phone?: string;
    };
    note?: string;
    voucher_code?: string;
    promo_code?: string;
    customer_address?: AddressObject;
    custom_field_answers?: CustomFieldAnswer[];
  }) => Promise<PreReservationResponse>;

  /**
   * Pose un code du champ unique sur une pré-réservation existante : le serveur y voit un code promo du pro
   * s'il en est un, sinon une carte cadeau ou un bon ; le code de l'autre nature déjà posé est gardé.
   * Absent : le tunnel essaie la carte cadeau, puis le code promo (`onApplyVoucher`, `onApplyPromo`).
   */
  onApplyCode?: (uuid: string, code: string) => Promise<CodeResponse>;

  /** Apply or update a voucher on an existing pre-reservation */
  onApplyVoucher?: (uuid: string, code: string) => Promise<VoucherResponse>;

  /** Remove a voucher from an existing pre-reservation */
  onRemoveVoucher?: (uuid: string) => Promise<VoucherResponse>;

  /** Apply or update a promo code on an existing pre-reservation */
  onApplyPromo?: (uuid: string, code: string) => Promise<PromoResponse>;

  /** Remove a promo code from an existing pre-reservation */
  onRemovePromo?: (uuid: string) => Promise<PromoResponse>;

  /** Cancel a pre-reservation */
  onCancelPreReservation?: (uuid: string) => Promise<void>;

  /** Confirm a pre-reservation (after payment) */
  onConfirmReservation?: (uuid: string) => Promise<{ success: boolean; message?: string }>;

  /** Confirm an OPTIONAL pre-reservation in pay-on-site mode (no online payment) */
  onConfirmPayOnSiteReservation?: (uuid: string) => Promise<{ success: boolean; message?: string }>;

  /** Autocomplete addresses (Google Places, public) — used for GEO_JSON zone places */
  onAutocompleteAddress?: (query: string) => Promise<AddressAutocompletePrediction[]>;

  /** Validate that an address falls inside the GEO_JSON zone of a pro_place. Throws if outside. */
  onValidateZoneAddress?: (proPlaceId: number, googlePlaceId: string) => Promise<{ address: AddressObject }>;
  /**
   * Load Stripe payment details for a paid pre-reservation.
   * The backend lazily creates the PaymentIntent and returns its client_secret.
   */
  onLoadPaymentDetails?: (uuid: string) => Promise<{
    client_secret?: string;
    stripe_connect_account_id: string;
    /** Payment methods carried by the Stripe intent — the logos shown at payment. */
    payment_method_types?: string[];
  }>;

  /** Poll the payment status after a Stripe confirmation (until PAYED / AUTHORIZED) */
  onPollPaymentStatus?: (uuid: string) => Promise<{ status: string; reservation_uuid?: string }>;

  /**
   * Carte « Course » (type RIDE) de l'étape prestations : la carte ne se réserve PAS dans le
   * tunnel classique — son CTA unique bascule vers le flux course (calculateur VTC) avec la
   * formule présélectionnée et le trajet par défaut prérempli par l'hôte. Optionnel et
   * DÉFENSIF : absent (hôte sans tunnel course), les cartes RIDE ne sont pas rendues du tout.
   */
  onSelectRidePrestation?: (prestation: BookingPrestation) => void;

  /**
   * Demande de devis d'une prestation sur devis (P3) : le circuit du formulaire de contact du site
   * (enregistrement, email au pro, captcha). Ne crée ni devis ni réservation. Absent : pas de bouton.
   */
  onSubmitQuoteRequest?: (request: BookingQuoteRequest) => Promise<void>;

  /** Numéro du pro, révélé au clic (binding protégé, captcha) pour « Appeler ». Absent : pas de bouton. */
  onRevealProPhone?: () => Promise<string | null>;
}

/** Demande de devis saisie dans le tunnel. */
export interface BookingQuoteRequest {
  prestation_id: number;
  prestation_name: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  message: string;
}

// ── Booking params (pro config) ─────────────────

export interface BookingParams {
  horizon_bookable?: number;
  rest_between_booking?: number;
  booking_step_min?: number;
  minimum_time_before_booking?: number;
  allow_multi_prestation?: boolean;
  allow_multi_member?: boolean;
  /** Tokens Stripe des moyens de paiement actifs sur le compte Connect du pro (réassurance, affichage seul). */
  allowed_payment_methods?: string[];
}

/** Prestation présélectionnée par un lien : formule (`price_id`) et quantité facultatives. */
export interface BookingPreselectLine {
  prestation_id: number;
  price_id?: number;
  quantity?: number;
}

// ── Filter config (from BookingWidget component) ─

/**
 * Preselected prestation carrying the choices already made outside the tunnel
 * (e.g. the "add to cart" modal on a custom-website prestation page): which
 * supplements are toggled and which duration/price the visitor picked.
 */
export interface BookingFilterConfig {
  prestation_ids?: number[];
  category_ids?: number[];
  place_ids?: number[];
  member_ids?: number[];
  preselect_ids?: number[];
  /**
   * Présélections avec formule et quantité (`?ps=12:45:2,13` de `/book`, `preselect-ids` du widget) :
   * une seule ligne complète ouvre le parcours express.
   */
  preselect_lines?: BookingPreselectLine[];
  /** Forçage du mode (`?entry=express|standard`), sinon déduit du panier et du catalogue. */
  entry_mode?: Exclude<BookingEntryMode, 'multi'>;
  /**
   * Jour `YYYY-MM-DD` sur lequel ouvrir la grille de créneaux (lien profond `?d=`). C'est un INDICE
   * d'ouverture, pas une contrainte : le visiteur reste libre de naviguer, et la grille avance
   * d'elle-même si la semaine visée ne porte aucun créneau pour le panier. Ignoré si la semaine
   * est passée — un lien partagé survit à la semaine qu'il visait.
   */
  initial_date?: string;
}

// ── Labels (overridable UI texts) ───────────────

/** Textes du tunnel adaptatif (P3), repris tels quels de `BOOKING_TUNNEL_FLOW` sous leur clé en minuscules. */
export type BookingFlowLabels = LowercasedLabels<(typeof MAPPED_TRANSLATION)['fr-FR']['BOOKING_TUNNEL_FLOW']>;

export interface BookingLabels extends PaymentTimelineLabels, BookingFlowLabels {
  step_service: string;
  step_place: string;
  step_member: string;
  step_resource: string;
  step_slot: string;
  step_checkout: string;
  continue: string;
  back: string;
  confirm: string;
  no_slots: string;
  today: string;
  quantity_label: string;
  supplements_label: string;
  duration_label: string;
  total_label: string;
  /** Total avant remise, au-dessus de la ligne de remise du récapitulatif de paiement. */
  subtotal_label: string;
  party_size_label: string;
  /** Plafond rappelé sous le libellé du sélecteur, `{count}` interpolé. */
  party_size_max_hint: string;
  party_size_decrease: string;
  party_size_increase: string;
  /** Suffixe de ligne de panier rappelant la taille du groupe, `{count}` interpolé. */
  party_size_suffix: string;
  /* Compteur de places restantes des créneaux « séance de groupe » (`{count}` interpolé). */
  slot_places_one: string;
  slot_places_other: string;
  slot_full: string;
  /** aria-label des créneaux grisés (capacité restante < groupe demandé), `{count}` interpolé. */
  slot_insufficient_places: string;
  /** Décomposition « N × prix unitaire » d'une ligne, `{count}` et `{price}` interpolés. */
  line_unit_breakdown: string;
  search_placeholder: string;
  all_categories: string;
  categories_scroll_prev: string;
  categories_scroll_next: string;
  /* Flèches ‹ › du calendrier de créneaux. Leur SEUL nom accessible est cet `aria-label` (le
     contenu visible est un chevron) : ils doivent nommer la NAVIGATION DE SEMAINE, jamais la
     progression dans le tunnel — sinon un lecteur d'écran annonce « Continuer » sur une flèche
     qui déplace le calendrier et efface le créneau choisi. */
  week_prev: string;
  week_next: string;
  choose: string;
  add_to_selection: string;
  /* Remplacement de la sélection. `{name}` = prestation de la carte, `{names}` = prestations du
     panier, entre guillemets. Bouton replié court ; l'action dépliée nomme les deux côtés
     (`replace_item` quand le panier ne contient qu'une prestation). */
  replace_short: string;
  replace_item: string;
  replace_selection: string;
  /* Pourquoi l'action remplace la sélection. */
  replace_reason_single: string;
  replace_reason_slot: string;
  replace_reason_place: string;
  /* Rappel sur la carte repliée d'une prestation non combinable avec le panier (multi-prestation). */
  replace_hint_slot: string;
  replace_hint_place: string;
  /* Badge des cartes déjà dans le panier. */
  in_selection: string;
  /* Indisponibilité réelle (grisé + raison) : jamais due au panier, qui propose un remplacement. */
  unavailable_no_slot: string;
  /* Porté par une formule sans créneau dans le sélecteur de durée : elle reste VISIBLE, grisée.
     « Aucun créneau » plutôt que « Complet » (qui laisserait croire à un agenda plein alors que la
     cause habituelle est une durée trop longue pour toute plage d'ouverture) et plutôt qu'un
     « Indisponible » qui ne dit pas CE QUI manque. « Créneau » est le mot que le client a déjà en
     tête à cette étape, et celui qu'emploient déjà la carte prestation et la grille horaire. */
  duration_unavailable: string;
  /*
   * Formule choisie en amont (site vitrine, lien de partage) qui n'est plus réservable à l'entrée
   * du tunnel. `{names}` = prestations concernées, séparées par des virgules. Le tunnel n'y
   * substitue JAMAIS une autre ligne de prix — ce serait changer le prix en silence — il écarte
   * l'article et ramène le client à l'étape prestations avec ce message.
   */
  preselect_unavailable: string;
  /* Prestation choisie en amont qu'aucun planning ne rend réservable en ligne. `{names}` idem. */
  preselect_not_bookable: string;
  place_unavailable_for_services: string;
  place_none_for_services: string;
  member_not_for_service: string;
  member_no_common_slot: string;
  member_not_at_place: string;
  resource_unavailable_at_place: string;
  selection_title: string;
  no_preference: string;
  choose_place: string;
  choose_member: string;
  choose_resource: string;
  resource_required: string;
  choose_slot: string;
  morning: string;
  afternoon: string;
  evening: string;
  price_from: string;
  on_quote: string;
  /** Libellé « Gratuit » (PriceType.FREE : prix ferme à 0 €). */
  free: string;
  /** Forme courte « prix indéterminé » pour les colonnes étroites (lignes du récap). */
  price_to_determine_short: string;
  /* Cartes « Course » (type RIDE) — vitrine, CTA vers le flux course (jamais de panier). */
  ride_badge: string;
  /** Passé (en minuscules) à formatRideCapacityLabel — libellé de capacité du pool. */
  ride_capacity_passengers: string;
  /** Passé (en minuscules) à formatRideCapacityLabel — libellé de capacité du pool. */
  ride_capacity_small_luggage: string;
  ride_capacity_large_luggage: string;
  /** Avec interpolation {price} — `ride_estimated_price` pré-formaté backend (jamais reformaté). */
  ride_price_from: string;
  /** Affiché quand aucune estimation `ride_estimated_price` n'est disponible. */
  ride_price_by_route: string;
  ride_book_cta: string;
  error_generic: string;
  remove: string;
  close: string;
  prestation_singular: string;
  prestations_plural: string;
  hide_detail: string;
  show_detail: string;
  read_more: string;
  read_less: string;
  watch_video: string;
  video_modal_title: string;
  photo_previous: string;
  photo_next: string;
  choose_per_service: string;
  choose_n_members: string;
  pick_n_members_remaining: string;
  decrease_quantity: string;
  increase_quantity: string;
  clear_search: string;
  booking_confirmed: string;
  booking_awaiting_confirmation: string;
  booking_awaiting_confirmation_message: string;
  success_manage_booking?: string;
  success_manage_hint?: string;
  add_supplement: string;
  remove_supplement: string;
  /* Checkout labels */
  checkout_title: string;
  your_info: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  note_label: string;
  note_placeholder: string;
  additional_info: string;
  voucher_label: string;
  voucher_apply: string;
  voucher_remove: string;
  voucher_placeholder: string;
  voucher_error_empty: string;
  voucher_error_expired: string;
  voucher_error_invalid: string;
  voucher_info_applied_prefix: string;
  // voucher_info_applied_generic : hérité de PaymentTimelineLabels.
  voucher_info_no_refund_on_cancel: string;
  /** Code vérifié pendant la création de la pré-réservation, qui est partie sans lui : à ré-appliquer. */
  voucher_error_retry: string;
  /** Code ni carte cadeau ni code promo : neutre, le champ unique a essayé les deux. */
  code_error_not_found: string;
  /* Champ code du récap et lignes du bloc `amounts` ({code}, {label}, {amount} remplacés à l'affichage). */
  code_remove: string;
  code_kind_promo: string;
  code_kind_voucher: string;
  code_applied: string;
  code_dropped: string;
  discount_line: string;
  manual_discount_line: string;
  voucher_line_amount: string;
  voucher_line_pack: string;
  voucher_line_prestation: string;
  pay_today_covered: string;
  remaining_on_site_sentence: string;
  /* Promotion automatique : condition du badge des cartes, rappel avant le formulaire ({amount}), bandeau
     quand la condition tombe à la pré-réservation ({label}). */
  promotion_condition_new_customer: string;
  promotion_condition_tags: string;
  promotion_condition_hours: string;
  promotion_condition_place: string;
  promotion_pending_new_customer: string;
  promotion_pending_tags: string;
  promotion_pending_hours: string;
  promotion_pending_place: string;
  promotion_rejected_new_customer: string;
  promotion_rejected_tags: string;
  promotion_rejected_generic: string;
  /* En-tête de progression ({step}, {index}, {total} remplacés), retour au site, cartes choisies, lieux
     groupés, erreur de chargement, chargement des créneaux ({name} remplacé). */
  progress_label: string;
  progress_counter: string;
  progress_counter_aria: string;
  back_to_site: string;
  /* Bandeau « panier retrouvé » ({names}, {ago} remplacés) et lien des suppléments repliés ({count}). */
  restored_selection_text: string;
  restored_selection_resume: string;
  restored_selection_restart: string;
  restored_selection_add_question: string;
  restored_selection_add: string;
  restored_selection_replace: string;
  supplements_show_more_one: string;
  supplements_show_more_other: string;
  chosen: string;
  remove_from_selection: string;
  place_group_establishment: string;
  place_group_remote: string;
  place_group_traveling: string;
  load_error: string;
  retry: string;
  slots_loading: string;
  /* Scan du QR d'une carte cadeau ou d'un lien promo (caméra du navigateur) */
  voucher_scan_open: string;
  voucher_scan_close: string;
  voucher_scan_starting: string;
  voucher_scan_scanning: string;
  /** QR lu, mais ni carte cadeau, ni lien promo, ni code. */
  voucher_scan_unrecognized: string;
  /** QR du PDF d'une carte cadeau : il ne contient pas le code. */
  voucher_scan_gift_card_pdf: string;
  voucher_scan_paused: string;
  voucher_scan_resume: string;
  voucher_scan_retry: string;
  voucher_scan_error_denied: string;
  voucher_scan_error_no_camera: string;
  voucher_scan_error_busy: string;
  /** Décodeur non chargé ou erreur inconnue. */
  voucher_scan_error_unavailable: string;
  /* Promo code labels (B2C) */
  promo_error_empty: string;
  promo_error_expired: string;
  promo_error_invalid: string;
  /** Hors happy hours sans détail serveur ; le backend fournit normalement les horaires exacts. */
  promo_error_outside_hours: string;
  promo_info_applied_prefix: string;
  promo_conditional_notice: string;
  payment_section: string;
  payment_locked: string;
  payment_finalizing: string;
  secure_payment: string;
  payment_methods_accepted: string;
  payment_redirect_failed: string;
  payment_redirect_pending: string;
  payment_redirect_unverified: string;
  payment_redirect_expired: string;
  payment_verifying: string;
  countdown_reserved: string;
  countdown_reserved_duration: string;
  countdown_expired: string;
  countdown_rechoose: string;
  cancellation_accept: string;
  cancellation_free_until: string;
  cancellation_until_no_payment: string;
  cancellation_attendance_commitment: string;
  cancellation_no_refund_after_confirm: string;
  amount_to_pay: string;
  remaining_on_site: string;
  // from_price_prefix + timeline_* : hérités de PaymentTimelineLabels (module partagé booking ↔ VTC).
  discount_label: string;
  required_field: string;
  invalid_email: string;
  invalid_phone: string;
  free_booking_confirm: string;
  pay_on_site_confirm: string;
  pay_online_section: string;
  pay_on_site_or_online: string;
  card_save_info: string;
  phone_country_code: string;
  phone_placeholder: string;
  /* Zone address (GEO_JSON places) */
  step_zone_address: string;
  zone_address_title: string;
  zone_address_subtitle: string;
  zone_address_placeholder: string;
  zone_address_outside_error: string;
  zone_address_required_error: string;
  zone_address_continue: string;
  zone_address_search_hint: string;
}

/**
 * Libellés du tunnel dans une langue (spec Parcours § 9) : clés `BOOKING_TUNNEL_*` de `@cfixe/shared`, un espace absent
 * de la langue retombe sur le français. L'hôte peut toujours surcharger un libellé (`labels` du tunnel).
 */
export function bookingLabelsFor(locale = 'fr-FR'): BookingLabels {
  const labels = tunnelTranslation(locale, 'BOOKING_TUNNEL_LABELS');
  const codes = tunnelTranslation(locale, 'BOOKING_TUNNEL_CODES');
  const nav = tunnelTranslation(locale, 'BOOKING_TUNNEL_NAV');
  return {
    ...lowercaseKeys(labels),
    ...lowercaseKeys(tunnelTranslation(locale, 'BOOKING_TUNNEL_FLOW')),
    code_remove: codes.REMOVE_CODE,
    code_kind_promo: codes.PROMO_CODE_KIND,
    code_kind_voucher: codes.VOUCHER_CODE_KIND,
    code_applied: codes.CODE_APPLIED,
    code_dropped: codes.CODE_DROPPED,
    discount_line: codes.DISCOUNT_LINE,
    manual_discount_line: codes.MANUAL_DISCOUNT_LINE,
    promotion_condition_new_customer: codes.PROMOTION_CONDITION_NEW_CUSTOMER,
    promotion_condition_tags: codes.PROMOTION_CONDITION_TAGS,
    promotion_condition_hours: codes.PROMOTION_CONDITION_HOURS,
    promotion_condition_place: codes.PROMOTION_CONDITION_PLACE,
    promotion_pending_new_customer: codes.PROMOTION_PENDING_NEW_CUSTOMER,
    promotion_pending_tags: codes.PROMOTION_PENDING_TAGS,
    promotion_pending_hours: codes.PROMOTION_PENDING_HOURS,
    promotion_pending_place: codes.PROMOTION_PENDING_PLACE,
    promotion_rejected_new_customer: codes.PROMOTION_REJECTED_NEW_CUSTOMER,
    promotion_rejected_tags: codes.PROMOTION_REJECTED_TAGS,
    promotion_rejected_generic: codes.PROMOTION_REJECTED_GENERIC,
    voucher_line_amount: codes.VOUCHER_LINE_AMOUNT,
    voucher_line_pack: codes.VOUCHER_LINE_PACK,
    voucher_line_prestation: codes.VOUCHER_LINE_PRESTATION,
    pay_today_covered: codes.PAY_TODAY_COVERED,
    remaining_on_site_sentence: codes.REMAINING_ON_SITE_SENTENCE,
    progress_label: nav.PROGRESS_LABEL,
    progress_counter: nav.PROGRESS_COUNTER,
    progress_counter_aria: nav.PROGRESS_COUNTER_ARIA,
    back_to_site: nav.BACK_TO_SITE,
    restored_selection_text: nav.RESTORED_SELECTION_TEXT,
    restored_selection_resume: nav.RESTORED_SELECTION_RESUME,
    restored_selection_restart: nav.RESTORED_SELECTION_RESTART,
    restored_selection_add_question: nav.RESTORED_SELECTION_ADD_QUESTION,
    restored_selection_add: nav.RESTORED_SELECTION_ADD,
    restored_selection_replace: nav.RESTORED_SELECTION_REPLACE,
    supplements_show_more_one: nav.SUPPLEMENTS_SHOW_MORE_ONE,
    supplements_show_more_other: nav.SUPPLEMENTS_SHOW_MORE_OTHER,
    chosen: nav.CHOSEN,
    remove_from_selection: nav.REMOVE_FROM_SELECTION,
    place_group_establishment: nav.PLACE_GROUP_ESTABLISHMENT,
    place_group_remote: nav.PLACE_GROUP_REMOTE,
    place_group_traveling: nav.PLACE_GROUP_TRAVELING,
    load_error: nav.LOAD_ERROR,
    retry: nav.RETRY,
    slots_loading: nav.SLOTS_LOADING,
    // Dérivé de la durée réelle de la pré-réservation (backend : même constante) — jamais en dur.
    countdown_reserved_duration: labels.COUNTDOWN_RESERVED_DURATION.replace(
      '{minutes}',
      String(Math.round(public_env.PRE_BOOKING_TIMEOUT_MS / 60_000)),
    ),
    // from_price_prefix + timeline_* : source unique de la frise (partagée booking ↔ VTC).
    ...paymentTimelineLabelsFor(locale),
  };
}

export const DEFAULT_LABELS: BookingLabels = bookingLabelsFor('fr-FR');

// ── Parcours (mesure) ───────────────────────────

/** Faits du parcours émis par le tunnel (spec Parcours § 13) ; l'hôte les relaie à sa mesure. */
export type BookingFunnelEventName =
  | 'booking_started'
  | 'booking_step'
  | 'booking_slot_selected'
  | 'booking_prereserved'
  | 'booking_abandoned'
  | 'begin_checkout';

export type BookingFunnelEvent = {
  [K in BookingFunnelEventName]: { name: K; params: SiteEventParams[K] };
}[BookingFunnelEventName];

/** Contexte d'un changement d'étape : rang et total (`visibleSteps`), absents pour la confirmation. */
export interface BookingStepChangeInfo {
  previous_step?: BookingStep;
  step_index?: number;
  step_total?: number;
}

// ── BookingTunnel props ─────────────────────────

export interface BookingTunnelProps {
  callbacks: BookingCallbacks;
  bookingParams?: BookingParams;
  filters?: BookingFilterConfig;
  /** Slug used as localStorage key for state persistence */
  professionalSlug?: string;
  professionalName?: string;
  /**
   * Code apporté par le lien (`?code=…` d'un QR de carte cadeau ou d'une campagne) : appliqué dès que le
   * panier est prêt, sans clic. Promo ou carte cadeau, le serveur tranche.
   */
  prefilledVoucherCode?: string;
  /** Code promo apporté par une campagne (`?promo=…`) : appliqué de la même façon. */
  prefilledPromoCode?: string;
  locale?: string;
  currency?: string;
  /** Stripe publishable key — required for paid bookings */
  stripePublishableKey?: string;
  onStepChange?: (step: BookingStep, info?: BookingStepChangeInfo) => void;
  /** Faits du parcours (ouverture, étapes, créneau, pré-réservation, abandon), pour la mesure de l'hôte. */
  onFunnelEvent?: (event: BookingFunnelEvent) => void;
  labels?: Partial<BookingLabels>;
  successMode?: 'message' | 'manage-inline';
  /**
   * Override base URL for flags (used when widget is embedded in a third-party site).
   * Defaults to public_env.API_ENDPOINT (which serves flags natively).
   */
  flagBaseUrl?: string;
  /** Passed by SSR wrapper to avoid Strict Mode URL-clearing race conditions */
  initialRedirectStatus?: string;
  initialBookingUuid?: string;
  /**
   * `false` = this tunnel must not resume a Stripe return (another tunnel of the page owns it, e.g. an
   * embed widget that did not claim it). Default `true`.
   */
  handleRedirectReturn?: boolean;
  /** Rendered on the first step's title line, right-aligned (e.g. /book mode switcher). */
  headerAccessory?: ReactNode;
  /**
   * Lien « Retour au site » de l'en-tête du tunnel, à toutes les étapes (site vitrine seulement : `/book`
   * et le widget n'ont pas de site autour). Le panier persisté garde la sélection.
   */
  backToSiteUrl?: string;
}

// ── Machine context ─────────────────────────────

export interface BookingContext {
  /* Data from initial load */
  prestations: BookingPrestation[];
  /* Photo du chargement initial (réservabilité sondée SANS panier). Sert à restaurer `prestations`
     quand le panier redevient vide : le recalcul live de bookability ne tourne qu'avec un panier,
     donc sans cette restauration les `is_bookable: false` du dernier check (« Incompatible avec
     votre sélection ») resteraient collés définitivement sur les cartes. */
  baselinePrestations: BookingPrestation[];
  categories: Category[];
  availablePlaces: PublicPlace[];
  availableMembers: TeamMember[];
  /* Tous les champs actifs du pro, tels que le serveur les envoie : jamais filtrés au chargement (ni les
     prestations ni le lieu ne sont encore choisis) — `useCustomFieldAnswers` évalue leur portée au checkout. */
  customFields: BookingCustomField[];
  /* Pro booking params returned by onLoadPrestations (multi-presta / multi-member flags…) */
  bookingParams: BookingParams;
  dataLoading: boolean;

  /* Mode du parcours, posé à l'ouverture (`SET_ENTRY_MODE`) puis multi dès la deuxième prestation. */
  entryMode: BookingEntryMode;

  /* Step 1 — Cart */
  cartItems: CartItem[];
  /* Résumé du panier calculé AVEC les codes posés (`appliedCodes`) : son bloc `amounts` alimente le récap
     tant que la pré-réservation n'existe pas, celui de `preReservationData` ensuite. */
  cartSummary: CartSummary | undefined;
  cartSummaryLoading: boolean;
  /*
   * Codes du champ « Code promo ou carte cadeau » : posés dès l'étape créneau, ils suivent le panier
   * (retours, modifications) et partent avec la pré-réservation, qui fait foi une fois créée.
   */
  appliedCodes: AppliedCodes;
  /*
   * Prestations dont la présélection amont (site vitrine) a été ÉCARTÉE parce que la formule
   * choisie n'est plus réservable. Non vide ⇒ bandeau à l'étape prestations ET interdiction de
   * sauter cette étape : le client doit pouvoir constater ce qui manque et rechoisir lui-même.
   */
  droppedPreselections: string[];
  /*
   * Prestations présélectionnées en amont (site vitrine) qu'aucun planning ne rend réservables en
   * ligne (`is_bookable === false`). Écartées comme les précédentes, avec leur propre bandeau : sans
   * cela, le client passait le lieu et l'intervenant pour tomber sur un calendrier vide.
   */
  unbookablePreselections: string[];

  /* Step 2 — Place */
  selectedPlaceId: number | undefined;

  /* Step 2.5 — Zone address (only when selected place is GEO_JSON) */
  customerAddress: AddressObject | undefined;

  /* Membres et ressources : choisis au-dessus du calendrier de l'étape créneau (défaut : sans préférence). */
  memberAssignments: MemberAssignment[];
  resourceAssignments: ResourceAssignment[];

  /* Step 4 — Slot */
  slots: BookableSlot[];
  slotsLoading: boolean;
  selectedSlot: BookableSlot | undefined;
  slotWeekStart: string | undefined;
  /* Retour forcé à l'étape créneau (créneau pris, pré-réservation expirée), effacé au prochain choix. */
  slotNotice: BookingSlotNotice | undefined;

  /* Prestation de la demande de devis en cours (étape `quote`). */
  quotePrestationId: number | undefined;

  /* Step 5 — Checkout */
  customerInfo:
    | {
        firstname: string;
        lastname: string;
        email: string;
        phone: string;
        note: string;
      }
    | undefined;
  preReservationUuid: string | undefined;
  preReservationData: PreReservationResponse | undefined;
  confirmedReservationUuid: string | undefined;

  /* Error */
  error: string | undefined;
}
